import * as Sentry from '@sentry/vue';
import { acceptHMRUpdate, defineStore } from 'pinia';

export const useClientDocumentStore = defineStore('clientDocument', () => {
    async function uploadFile(file, clientIdOrUuid, onUploadProgress) {
        // Support both client ID (edit mode) and UUID (create mode)
        // Backend creates client if UUID doesn't exist
        const url = `/api/v1/clients/${clientIdOrUuid}/document-bundles/upload`

        const formData = new FormData()
        formData.append('name', file.name)
        formData.append('file', file)

        return await new Promise((resolve, reject) => {
            axios.post(url, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                },
                onUploadProgress: onUploadProgress
            }).then(response => {
                if (response.status === 201) {
                    const redirectUrl = response.headers.location
                    return axios.get(redirectUrl).then(res => resolve(res)).catch(err => reject(err))
                }
                // Resolve with response for non-201 statuses
                resolve(response)
            }).catch(error => {
                // Handle upload error
                Sentry.captureException(error)
                reject(error)
            })
        });
    }

    async function sendForSignature(client_id, payload) {
        const url = `/api/v1/clients/${client_id}/document-bundles/send-for-signature`

        return await new Promise((resolve, reject) => {
            axios.post(url, payload)
                .then(response => {
                    if (response.status === 201 || response.status === 302 || response.status === 303) {
                        const redirectUrl = response.headers.location
                        if (redirectUrl) {
                            return axios.get(redirectUrl).then(res => resolve(res)).catch(err => reject(err))
                        }
                    }
                    resolve(response)
                })
                .catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
        })
    }

    async function cancel(client_id, bundle_id) {
        const url = `/api/v1/clients/${client_id}/document-bundles/${bundle_id}/cancel`

        return await new Promise((resolve, reject) => {
            axios.post(url)
                .then(response => {
                    const status = response.status
                    const location = response.headers && response.headers.location
                    if ((status === 303) && location) {
                        return axios.get(location).then(res => resolve(res)).catch(err => reject(err))
                    }
                    resolve(response)
                })
                .catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
        })
    }

    async function deleteBundle(client_id, bundle_id) {
        const url = `/api/v1/clients/${client_id}/document-bundles/${bundle_id}`

        return await new Promise((resolve, reject) => {
            axios.delete(url)
                .then(response => {
                    const status = response.status
                    const location = response.headers && response.headers.location
                    if ((status === 303 || status === 302 || status === 301) && location) {
                        return axios.get(location).then(res => resolve(res)).catch(err => reject(err))
                    }
                    resolve(response)
                })
                .catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
        })
    }

    async function updateDocumentTags(client_id, bundle_id, document_id, tags) {
        const url = `/api/v1/clients/${client_id}/document-bundles/${bundle_id}/documents/${document_id}`
        return await new Promise((resolve, reject) => {
            axios.patch(url, { tags })
                .then(response => {
                    // Some endpoints redirect to a show URL — follow the Location header
                    const status = response.status
                    const location = response.headers && response.headers.location
                    if ((status === 303 || status === 302 || status === 301) && location) {
                        return axios.get(location).then(res => resolve(res)).catch(err => reject(err))
                    }
                    resolve(response)
                })
                .catch(error => {
                    Sentry.captureException(error)
                    reject(error)
                })
        })
    }

    return {
        uploadFile,
        sendForSignature,
        cancel,
        deleteBundle,
        updateDocumentTags
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useClientDocumentStore, import.meta.hot))
}
